import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Flex,
  Text,
  Stat,
  StatLabel,
  StatNumber,
  StatGroup,
  Button,
  Input,
  Select,
  Spinner,
  HStack,
} from "@chakra-ui/react";
import Chart from "react-apexcharts";
import { useAuth } from "../../context/AuthContext";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import ChannelDetailModal from "./ChannelDetailModal";
import { supabase } from "../../lib/supabaseClient";
import SettlementSection from "./SettlementSection";
import { fetchWithToken } from "../../lib/api";

function getBaseDate() {
  const today = new Date();
  today.setDate(today.getDate() - 3);
  return today;
}
function getDefaultDates(period = 7) {
  const base = getBaseDate();
  const end = base.toISOString().slice(0, 10);
  const startDateObj = new Date(base);
  startDateObj.setDate(base.getDate() - period);
  const start = startDateObj.toISOString().slice(0, 10);
  return { start, end };
}

const Partners = () => {
  const { user } = useAuth();
  const [inputDateRange, setInputDateRange] = useState(getDefaultDates(7));
  const [dateRange, setDateRange] = useState(getDefaultDates(7));
  const [period, setPeriod] = useState(7);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [chart1Data, setChart1Data] = useState({
    categories: [],
    revenue: [],
    views: [],
    premiumViews: [],
  });
  const [channelList, setChannelList] = useState([]);
  const [channelLoading, setChannelLoading] = useState(false);
  const [channelError, setChannelError] = useState("");
  const [inputSorting, setInputSorting] = useState("TOTAL_VIEWS");
  const [pageNumber, setPageNumber] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [inputSearchKeywords, setInputSearchKeywords] = useState("");
  const [searchParams, setSearchParams] = useState({
    dateRange: getDefaultDates(7),
    searchKeywords: "",
    sorting: "TOTAL_VIEWS",
    pageNumber: 1,
  });
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [channelModalOpen, setChannelModalOpen] = useState(false);
  const [shortsContracts, setShortsContracts] = useState([]);
  const [selectedContractId, setSelectedContractId] = useState("all");
  const [showSettlement, setShowSettlement] = useState(false);
  const [tempPage, setTempPage] = useState(pageNumber.toString());

  // shorts_contract 테이블 데이터 가져오기
  useEffect(() => {
    const fetchContracts = async () => {
      const { data, error } = await supabase
        .from("shorts_contract")
        .select("id, contractor, partner, contractor_name, email")
        .eq("partner", user?.email)
        .order("created_date", { ascending: false });

      if (data) {
        console.log(data);
        setShortsContracts([
          {
            id: "all",
            contractor_name: "전체",
            contractor: "all",
            email: "all",
            partner: "all",
          },
          ...data,
        ]);
      }
    };
    if (user?.email) fetchContracts();
  }, [user]);

  // 상단 요약/그래프 데이터
  useEffect(() => {
    if (!searchParams.dateRange) return;
    const fetchSummary = async () => {
      setLoading(true);
      try {
        const accessToken = localStorage.getItem("access_token");
        const params = new URLSearchParams({
          start_date: searchParams.dateRange.start,
          end_date: searchParams.dateRange.end,
        });
        const response = await fetchWithToken(
          `https://plpl.rhoonart.co.kr/profits/total/partner?${params.toString()}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/json",
            },
          }
        );
        const result = await response.json();
        setSummary(result.data.summary);
        const dailyStats = result?.data?.dailyStats || [];
        setChart1Data({
          categories: dailyStats.map((d) => d.date),
          revenue: dailyStats.map((d) => d.revenueKrw),
          views: dailyStats.map((d) => d.views),
          premiumViews: dailyStats.map((d) => d.premiumViews),
        });
      } catch (e) {
        setSummary(null);
        setChart1Data({
          categories: [],
          revenue: [],
          views: [],
          premiumViews: [],
        });
      } finally {
        setLoading(false);
      }
    };
    fetchSummary();
  }, [searchParams.dateRange]);

  // 채널별 표 fetch
  useEffect(() => {
    const fetchChannelList = async () => {
      setChannelLoading(true);
      setChannelError("");
      try {
        const accessToken = localStorage.getItem("access_token");
        const params = new URLSearchParams({
          start_date: searchParams.dateRange.start,
          end_date: searchParams.dateRange.end,
          sorting: searchParams.sorting,
          order: "DESC",
          pageNumber: searchParams.pageNumber,
          searchKeywords: searchParams.searchKeywords,
          profitKRW: 500,
        });
        if (selectedContractId && selectedContractId !== "all") {
          const selectedContract = shortsContracts.find(
            (c) => c.id === selectedContractId
          );
          if (selectedContract && selectedContract.email) {
            const { data, error } = await supabase
              .from("user")
              .select("id")
              .eq("email", selectedContract.email)
              .maybeSingle();
            if (data && data.id) {
              params.append("user_id", data.id);
            }
          }
        }
        const response = await fetchWithToken(
          `https://plpl.rhoonart.co.kr/profits/channel?${params.toString()}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/json",
            },
          }
        );
        const result = await response.json();
        setChannelList(result.data.channelList);
        setPagination(result.data.pagination);
      } catch (e) {
        setChannelError("데이터 없음");
        setChannelList([]);
        setPagination(null);
      } finally {
        setChannelLoading(false);
      }
    };
    fetchChannelList();
  }, [searchParams, shortsContracts, selectedContractId]);

  useEffect(() => {
    setTempPage(pageNumber.toString());
  }, [pageNumber]);
  const handlePageInput = (e) => setTempPage(e.target.value);
  const handlePageMove = () => {
    if (/^\d+$/.test(tempPage)) {
      const page = parseInt(tempPage);
      if (page >= 1 && page <= (pagination?.totalPages || 1))
        handlePageChange(page);
      else setTempPage(pageNumber.toString());
    } else setTempPage(pageNumber.toString());
  };

  // 검색 실행 함수
  const handleSearch = () => {
    setSearchParams({
      dateRange: inputDateRange,
      searchKeywords: inputSearchKeywords,
      sorting: inputSorting,
      pageNumber: 1,
    });
    setPageNumber(1);
  };

  // 기간 변경 핸들러
  const handlePeriodChange = (days) => {
    setPeriod(days);
    const newRange = getDefaultDates(days);
    setInputDateRange(newRange);
    setSearchParams((prev) => ({
      ...prev,
      dateRange: newRange,
      pageNumber: 1,
    }));
    setPageNumber(1);
  };

  // 페이지네이션 버튼 클릭 시
  const handlePageChange = (newPage) => {
    setSearchParams((prev) => ({ ...prev, pageNumber: newPage }));
    setPageNumber(newPage);
  };

  // 정렬 변경
  const handleSortingChange = (value) => {
    setInputSorting(value);
    setSearchParams((prev) => ({
      ...prev,
      sorting: value,
      pageNumber: 1,
    }));
    setPageNumber(1);
  };

  // 채널 row 클릭 핸들러
  const handleChannelRowClick = (channel) => {
    setSelectedChannel(channel);
    setChannelModalOpen(true);
  };

  // 카드 데이터
  const stats = [
    {
      label: "총 수익 (KRW)",
      value: summary?.totalRevenueKrw
        ? `₩${summary.totalRevenueKrw.toLocaleString()}`
        : "-",
    },
    {
      label: "총 수익 (USD)",
      value: summary?.totalRevenueUsd
        ? `$ ${summary.totalRevenueUsd.toLocaleString()}`
        : "-",
    },
    {
      label: "총 영상 수",
      value: summary?.totalVideos
        ? `${summary.totalVideos.toLocaleString()}개`
        : "-",
    },
    {
      label: "총 조회수",
      value: summary?.totalViews
        ? `${summary.totalViews.toLocaleString()}회`
        : "-",
    },
  ];

  const chart1 = {
    options: {
      chart: { id: "revenue-views", toolbar: { show: true } },
      xaxis: { categories: chart1Data.categories },
      colors: ["#1e90ff", "#00e676", "#ffc107"],
      stroke: { width: 4 },
      legend: { show: true, position: "bottom" },
      yaxis: {
        labels: {
          formatter: function (value) {
            return value.toLocaleString();
          },
        },
      },
      tooltip: {
        y: {
          formatter: function (value) {
            return value.toLocaleString();
          },
        },
      },
    },
    series: [
      { name: "수익(KRW)", data: chart1Data.revenue },
      { name: "조회수", data: chart1Data.views },
      { name: "[프리미엄] 조회수", data: chart1Data.premiumViews },
    ],
  };

  const accessToken =
    typeof window !== "undefined" ? localStorage.getItem("access_token") : "";

  return (
    <Box bg="gray.50" minH="100vh" py={8}>
      <Container maxW="container.xl">
        {/* 경고 배너 */}
        <Box
          bg="red.500"
          color="white"
          p={3}
          borderRadius="md"
          mb={2}
          fontWeight="bold"
          fontSize="md"
          textAlign="center"
        >
          ※ 본 화면은 파트너에게만 공유되는 화면이며, 개인 정보를 담고 있을 수
          있습니다. 해당 화면 및 화면에 포함 된 정보는 외부에 유출 및 노출이
          금지되어있으니 각별히 유의해주세요
        </Box>
        {/* 정산 내역 보기 버튼 오른쪽 끝 배치 */}
        <Flex justify="flex-end" mb={4}>
          {!showSettlement && (
            <Button
              colorScheme="blue"
              onClick={() => setShowSettlement(true)}
              minW="140px"
            >
              정산 내역 보기
            </Button>
          )}
        </Flex>

        {showSettlement && (
          <SettlementSection
            contractors={shortsContracts
              .filter((c) => c.id !== "all")
              .map((c) => c.contractor)}
            contractorsInfo={shortsContracts.filter((c) => c.id !== "all")}
            onClose={() => setShowSettlement(false)}
          />
        )}
        {/* 요약 카드 */}
        <StatGroup mb={6} gap={4}>
          {stats.map((stat, idx) => (
            <Stat
              key={stat.label}
              px={6}
              py={4}
              bg="white"
              borderRadius="xl"
              boxShadow="md"
              minW="220px"
            >
              <StatLabel fontSize="md" color="gray.600">
                {stat.label}
              </StatLabel>
              <StatNumber fontSize="2xl" color="black">
                {stat.value}
              </StatNumber>
            </Stat>
          ))}
        </StatGroup>
        {/* 그래프 */}
        <Box bg="white" borderRadius="xl" boxShadow="md" p={4} mb={8}>
          <Chart
            options={chart1.options}
            series={chart1.series}
            type="line"
            height={320}
          />
        </Box>
        {/* 기간/검색 필터 */}
        <Flex align="center" gap={4} mb={4}>
          <Select
            value={inputSorting}
            onChange={(e) => handleSortingChange(e.target.value)}
            maxW="180px"
            bg="gray.50"
          >
            <option value="TOTAL_VIEWS">조회수순</option>
            <option value="REVENUE_KRW">KRW 수익순</option>
          </Select>
          <Select
            value={period}
            onChange={(e) => handlePeriodChange(Number(e.target.value))}
            maxW="120px"
            bg="white"
          >
            <option value={7}>최근 7일</option>
            <option value={14}>최근 14일</option>
            <option value={21}>최근 21일</option>
            <option value={30}>최근 30일</option>
          </Select>
          <Input
            type="date"
            value={inputDateRange.start}
            onChange={(e) =>
              setInputDateRange((r) => ({ ...r, start: e.target.value }))
            }
            maxW="160px"
            bg="white"
          />
          <Text>~</Text>
          <Input
            type="date"
            value={inputDateRange.end}
            onChange={(e) =>
              setInputDateRange((r) => ({ ...r, end: e.target.value }))
            }
            maxW="160px"
            bg="white"
          />
          <Input
            placeholder="채널명 검색"
            value={inputSearchKeywords}
            onChange={(e) => setInputSearchKeywords(e.target.value)}
            maxW="220px"
            bg="white"
          />
          <Button colorScheme="teal" onClick={handleSearch}>
            검색
          </Button>
          <Box flex={1} />
          <Select
            value={selectedContractId}
            onChange={(e) => setSelectedContractId(e.target.value)}
            maxW="220px"
            bg="white"
          >
            {shortsContracts.map((c, idx) => (
              <option key={c.id || c.contractor || idx} value={c.id}>
                {c.contractor_name}
                {c.id !== "all" && c.email ? ` (${c.email})` : ""}
              </option>
            ))}
          </Select>
        </Flex>
        {/* 채널별 정보 표 */}
        <Box bg="white" borderRadius="xl" boxShadow="md" p={4} mb={8}>
          <Flex mb={4} align="center" gap={4} justify="space-between">
            <Text color="gray.500" fontSize="sm">
              채널별 상세 수익 및 통계
            </Text>
          </Flex>
          <Box overflowX="auto">
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead style={{ background: "#f7fafc" }}>
                <tr>
                  <th style={{ padding: "12px" }}>#</th>
                  <th>채널 정보</th>
                  <th>영상 수</th>
                  <th>
                    조회수
                    <br />
                    (프리미엄 조회수)
                  </th>
                  <th>
                    시청 시간
                    <br />
                    (프리미엄 시청 시간)
                  </th>
                  <th>US 예상 수익</th>
                  <th>KRW 예상 수익 (단가)</th>
                </tr>
              </thead>
              <tbody>
                {channelLoading ? (
                  <tr>
                    <td
                      colSpan={7}
                      style={{ textAlign: "center", padding: 32 }}
                    >
                      <Spinner size="lg" color="teal.400" />
                    </td>
                  </tr>
                ) : channelError ? (
                  <tr>
                    <td
                      colSpan={7}
                      style={{ textAlign: "center", color: "red", padding: 32 }}
                    >
                      {channelError}
                    </td>
                  </tr>
                ) : channelList.length === 0 ? (
                  <tr>
                    <td
                      colSpan={7}
                      style={{
                        textAlign: "center",
                        color: "#888",
                        padding: 32,
                      }}
                    >
                      데이터가 없습니다.
                    </td>
                  </tr>
                ) : (
                  channelList.map((channel, idx) => (
                    <tr
                      key={idx}
                      style={{
                        borderBottom: "1px solid #eee",
                        cursor: "pointer",
                      }}
                      onClick={() => handleChannelRowClick(channel)}
                    >
                      <td style={{ textAlign: "center", fontWeight: 600 }}>
                        {idx + 1}
                      </td>
                      <td
                        style={{
                          padding: "12px",
                          display: "flex",
                          alignItems: "center",
                          gap: 12,
                        }}
                      >
                        <img
                          src={channel.channelThumbnailUrl}
                          alt={channel.channelTitle}
                          style={{
                            width: 48,
                            height: 48,
                            borderRadius: "50%",
                            objectFit: "cover",
                          }}
                        />
                        <div>
                          <div style={{ fontWeight: 600 }}>
                            {channel.channelTitle || (
                              <>
                                채널정보확인불가
                                <span
                                  style={{
                                    background: "#e53e3e",
                                    color: "white",
                                    borderRadius: "8px",
                                    fontSize: "11px",
                                    padding: "2px 8px",
                                    marginLeft: "8px",
                                    verticalAlign: "middle",
                                    fontWeight: 700,
                                  }}
                                >
                                  미등록 채널
                                </span>
                              </>
                            )}
                          </div>
                          <div style={{ color: "#888", fontSize: 13 }}>
                            {channel.channelId}
                          </div>
                        </div>
                      </td>
                      <td style={{ textAlign: "center" }}>
                        {channel.totalVideos}
                      </td>
                      <td style={{ textAlign: "center" }}>
                        {channel.totalViews?.toLocaleString()}
                        <br />
                        <span style={{ color: "#4fd1c5", fontSize: 13 }}>
                          ({channel.totalPremiumViews?.toLocaleString()})
                          {/*{(channel.premiumViewRatio * 100).toFixed(2)}%)*/}
                        </span>
                      </td>
                      <td style={{ textAlign: "center" }}>
                        {channel.totalWatchTime?.toLocaleString()}
                        <br />
                        <span style={{ color: "#4fd1c5", fontSize: 13 }}>
                          ({channel.totalPremiumWatchTime?.toLocaleString()})
                          {/*{(channel.premiumWatchTimeRatio * 100).toFixed(2)}%)*/}
                        </span>
                      </td>
                      <td
                        style={{ textAlign: "center", whiteSpace: "pre-line" }}
                      >
                        $ {channel.estimatedRevenueUsd?.toLocaleString()}
                        <br />
                        <span style={{ color: "#4fd1c5", fontSize: 13 }}>
                          {/*$ {channel.usdRevenuePerView}*/}
                        </span>
                      </td>
                      <td
                        style={{ textAlign: "center", whiteSpace: "pre-line" }}
                      >
                        ₩ {channel.estimatedRevenueKrw?.toLocaleString()}
                        <br />
                        <span style={{ color: "#4fd1c5", fontSize: 13 }}>
                          ₩ {channel.krwRevenuePerView}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </Box>
          {/* 페이지네이션 */}
          {pagination && (
            <Flex justify="center" align="center" mt={6} gap={2}>
              <Button
                onClick={() => handlePageChange(1)}
                isDisabled={pageNumber === 1}
                size="sm"
                variant="outline"
              >
                처음
              </Button>
              <Button
                onClick={() => handlePageChange(Math.max(1, pageNumber - 1))}
                isDisabled={pageNumber === 1}
                size="sm"
                variant="outline"
              >
                이전
              </Button>
              <HStack spacing={1} mx={2}>
                <Input
                  value={tempPage}
                  onChange={handlePageInput}
                  size="sm"
                  width="50px"
                  textAlign="center"
                  fontSize="sm"
                />
                <Button
                  size="sm"
                  onClick={handlePageMove}
                  colorScheme="gray"
                  variant="outline"
                  minW="40px"
                  px={2}
                  color="gray.600"
                  borderColor="gray.300"
                  _hover={{ bg: "gray.100", borderColor: "gray.400" }}
                >
                  이동
                </Button>
                <Text fontWeight="bold">/ {pagination.totalPages}</Text>
              </HStack>
              <Button
                onClick={() =>
                  handlePageChange(
                    Math.min(pagination.totalPages, pageNumber + 1)
                  )
                }
                isDisabled={pageNumber === pagination.totalPages}
                size="sm"
                variant="outline"
              >
                다음
              </Button>
              <Button
                onClick={() => handlePageChange(pagination.totalPages)}
                isDisabled={pageNumber === pagination.totalPages}
                size="sm"
                variant="outline"
              >
                마지막
              </Button>
            </Flex>
          )}
        </Box>
        {/* 채널 상세 모달 */}
        <ChannelDetailModal
          isOpen={channelModalOpen}
          onClose={() => setChannelModalOpen(false)}
          channel={selectedChannel}
          dateRange={searchParams.dateRange}
          accessToken={accessToken}
        />
      </Container>
    </Box>
  );
};

export default Partners;
